import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const CollectionReceivedBarChart = ({ data = [] , fyLabel = ""}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ height: 320, textAlign: "center", paddingTop: 120 }}>
        No data available
      </div>
    );
  }

  /* ================= FY LABELS ================= */

  const getCurrentAndPreviousFYLabels = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // Jan = 0

    const currentFYStart = month < 3 ? year - 1 : year;

    return {
      currentFY: `${currentFYStart}-${String(currentFYStart + 1).slice(-2)}`,
      previousFY: `${currentFYStart - 1}-${String(currentFYStart).slice(-2)}`
    };
  };

  const { currentFY, previousFY } = getCurrentAndPreviousFYLabels();

  /* ================= MODE DETECTION ================= */

  // ✅ If FY_SELECTED exists → Single FY mode
  const isSingleFY = data.some(d => d.FY_SELECTED !== undefined);

  /* ================= NORMALIZE ================= */

  const normalized = data.map(d => ({
    label: d.customerName ?? d.name ?? "Unknown",
    fyCurrent: Number(d.FY_CURRENT ?? 0),
    fyPrevious: Number(d.FY_PREVIOUS ?? 0),
    fySelected: Number(d.FY_SELECTED ?? 0)
  }));

  /* ================= SORT ================= */

  const sorted = normalized.sort((a, b) =>
    isSingleFY
      ? b.fySelected - a.fySelected
      : b.fyCurrent - a.fyCurrent
  );

  const hasPreviousData = sorted.some(d => d.fyPrevious > 0);

  /* ================= LABELS ================= */

  const labels = sorted.map(d =>
    d.label.length > 18 ? d.label.slice(0, 18) + "…" : d.label
  );

  /* ================= DATASETS ================= */

  const datasets = isSingleFY
    ? [
        {
          label: fyLabel || "FY",
          data: sorted.map(d => d.fySelected),
          backgroundColor: "#1976d2",
          borderRadius: 6,
          barThickness: 16,
          maxBarThickness: 20
        }
      ]
    : [
        {
          label: currentFY,
          data: sorted.map(d => d.fyCurrent),
          backgroundColor: "#1976d2",
          borderRadius: 6,
          barThickness: 16,
          maxBarThickness: 20
        },
        {
          label: previousFY,
          data: sorted.map(d => d.fyPrevious),
          backgroundColor: "#fbc02d",
          borderRadius: 6,
          barThickness: 16,
          maxBarThickness: 20,
          hidden: !hasPreviousData
        }
      ];

  const chartData = {
    labels,
    datasets
  };

  /* ================= OPTIONS ================= */

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: false,

    layout: {
      padding: {
        left: 20,
        right: 30
      }
    },

    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end"
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (!ctx.raw) return null;
            return `${ctx.dataset.label}: ${ctx.raw.toLocaleString("en-IN")} L`;
          }
        }
      },
      datalabels: {
        display: true,
        color: "#ffffff",
        anchor: "center",
        align: "center",
        offset: 0,
        clamp: true,
        clip: false,
        formatter: (value) => `${Number(value || 0).toFixed(2)} L`,
        font: {
          weight: "bold",
          size: 10
        }
      }
    },

    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (v) => `${v.toFixed(0)} L`
        }
      },
      y: {
        ticks: {
          autoSkip: false
        },
        grid: {
          drawTicks: false
        }
      }
    }
  };

  /* ================= RENDER ================= */

  return (
    <div style={{ height: 400 }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default CollectionReceivedBarChart;
